// sealy HTML sitemap body (/cms/sitemap/): the nested page-link tree the
// legacy sitemap.html rendered from treebeard's annotated list.
import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import { templates } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import styles from "./index.module.scss";

type IEntry = templates.SiteMapDocumentTemplate["entries"][number];

type ITreeNode = {
    title: string;
    url: string;
    children: ITreeNode[];
};

// Rebuild the flat, level-tagged page list (treebeard annotated order) into a
// nested tree so React can emit balanced <ul>/<li> markup. A recursive
// *component* is fine here; only recursive reactivated *types* are avoided.
// Exported for the level-jump unit test; not part of the template contract.
export const buildTree = (entries: readonly IEntry[]): ITreeNode[] => {
    const roots: ITreeNode[] = [];
    const stack: ITreeNode[] = [];
    for (const entry of entries) {
        const node: ITreeNode = {
            title: entry.title,
            url: entry.url,
            children: [],
        };
        // `level` is 0-based (treebeard depth-minus-start_depth); trim the
        // stack back to this node's parent depth before re-pushing. Levels
        // can jump by MORE than +1: the annotated list runs over a FILTERED
        // queryset, so a hidden/not-live parent with a visible descendant
        // yields e.g. level 0 -> level 2, leaving a hole at the parent slot
        // after the resize. The guard treats the missing parent as "no
        // parent" and roots the orphan (the legacy Django template rendered
        // mis-nested-but-valid HTML in that scenario; it never crashed, and
        // neither may we -- an exception here is an SSR 500).
        stack.length = entry.level;
        // .at(-1) rather than [length - 1]: after a hole-creating resize the
        // slot is empty, and .at is typed `ITreeNode | undefined` so the
        // guard below type-checks honestly.
        const parent = stack.at(-1);
        if (parent) {
            parent.children.push(node);
        } else {
            roots.push(node);
        }
        stack.push(node);
    }
    return roots;
};

const SiteMapNodes = (
    props: Immutable<{ nodes: readonly ITreeNode[] }>,
): React.JSX.Element => (
    <ul>
        {props.nodes.map((node) => (
            <li key={node.url}>
                <a href={node.url}>{node.title}</a>
                {node.children.length > 0 && (
                    <SiteMapNodes nodes={node.children} />
                )}
            </li>
        ))}
    </ul>
);

type IEntries = templates.SiteMapDocumentTemplate["entries"];

export const SiteMapPageBody = (
    props: Immutable<{ entries: IEntries }>,
): React.JSX.Element => {
    const tree = buildTree(props.entries);
    return (
        <div className={concatClassNames(["l-capped-width", styles.sitemap])}>
            <div>
                <h2>{t`Sitemap`}</h2>
            </div>
            <div className={styles.sitemapLinks}>
                <SiteMapNodes nodes={tree} />
            </div>
        </div>
    );
};
